import { MatDialog } from '@angular/material/dialog';
import { Account, User } from '@mitel/cloudlink-sdk';
import { AppInjector } from '../../app-injector';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { StatusType } from '../constants/statusType.enum';
import { IntegrationItemServiceInf, StatusString, UserLicense } from '../interfaces/integrations';
import { FeatureToggleService } from './feature-toggle.service';
import { MiccConfigDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/micc-config-dialog/micc-config-dialog.component';
import { MiccbApiService, ServiceTypes } from '@mitel-internal/miccb-components';
import { AccountsService } from './accounts.service';
import { lastValueFrom } from 'rxjs';

const logContext = 'IntegMiccService';

/**
 * Maps to integrations.ts productCode: "MiCC",
 */
export class IntegMiccService implements IntegrationItemServiceInf {

    public dialog: MatDialog;
    private featureToggleService: FeatureToggleService;
    private miccbApiService: MiccbApiService;
    private accountsService: AccountsService;

    initServices() {
      if ( !this.dialog )
        this.dialog = AppInjector.injector.get(MatDialog);

      if ( !this.featureToggleService )
        this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

      if ( !this.miccbApiService )
        this.miccbApiService = AppInjector.injector.get(MiccbApiService);

      if ( !this.accountsService )
        this.accountsService = AppInjector.injector.get(AccountsService);
    }

    getConfigurationDialog() {
      return MiccConfigDialogComponent;
    }

    isSetupComplete(account: Account): Promise<boolean> {
      return Promise.resolve(true);
    }

    handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[] | undefined): Promise<any> {
        return Promise.resolve();
    }
    /**
     * If there is a problem with the product installation, this will indicate that.
     * @param account
     * @returns undefined if no problems, else returns severity/message in a StatusString object
     */
    getStatusString(account: Account): Promise<StatusString> {
      this.initServices();
      return this.miccbApiService.getActiveMiccbId(account.accountId).toPromise()
        .then(
          async (miccbId) => {
            // is this MiCC-B's CloudLink Damion started?
            const component = await this.miccbApiService.getMiccbComponents().toPromise().then( data => data.find(c => c.componentId === miccbId));
            const started = !!component?.services?.find(s => s.serviceType === ServiceTypes.ADMIN_API)?.serviceId;
            if (!started) {
              return {status: 'integrations.micc-config.status.cld-not-started',
                type: StatusType.caution
              }
            }
            return undefined;
          },
          async err => {
            console.log(`${logContext}.getStatusString error`, err);
            if (err.message.includes('multiple MiCC-B components found')) {
              return {
                status: 'integrations.micc-config.status.multiple-micc-found',
                type: StatusType.error
              };
            } else if (err.message.includes('no MiCC-B component found')) {
              const tags = await this.accountsService.getAccountTagsById(account.accountId);
              const miccbVersion = tags?.MiCCBProductVersion;  // This tag is managed by the MiCC-B server.
              // Should there be a CLD installed?
              const isCldSupported = !!miccbVersion && this.isVersionGE(miccbVersion, '10.1.0');
              if (isCldSupported) {
                // MiCC-B reported that it has a version which contains CLD.
                // System Manager microservice did not find a MiCC-B component,
                // so we conclude that the MiCC-B upgrade/install's cloud registration failed.
                return {
                  status: 'integrations.micc-config.status.cld-install-failed',
                  type: StatusType.caution
                };
              }
            } else {
              return undefined;
            }
          }
        )
    }

    /**
     *
     * @param versionToTest
     * @param targetVersion
     * @returns true if versionToTest equal or greater than the target version
     */
    public isVersionGE(versionToTest: string, targetVersion: string): boolean {
      return versionToTest?.localeCompare(targetVersion, undefined, { numeric: true, sensitivity: 'base' }) >= 0;
  }

  	/**
     * Test validity of the configuration.
     * @param account
     * @returns true if the configuration is no longer valid or there are multiple MiCC servers
     */
    needConfigUpdate(account: Account): Promise<boolean> {
      this.initServices();
      return (lastValueFrom(this.miccbApiService.getMiccbComponents())
        .then((data) => {
          // If there is more than one MiCC server, return true so the user can select the server
          if (data?.length > 1) return true;
          return this.miccbApiService.getActiveMiccbId(account.accountId).toPromise()
            .then(() => false,
              err => {
                if (err.message.includes('multiple MiCC-B components found')) {
                  return true;
                } else {
                  return false;
                }
              }
            )
        })
      )
    }

    getConfigButtonLabel(account: Account): Promise<string> {
        return undefined;
    }
    isButtonDisabled(account: Account): Promise<boolean> {
        return Promise.resolve(false);
    }
	canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initServices();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_MICC_CONFIRMATION } });
		return dialogRef.afterClosed().toPromise().then(result => {
			return result;
		});
	}


  getUserLicense(user: User, account: Account): Promise<UserLicense> {
    this.initServices();
    let userLicense: UserLicense = {
      code: "MiCCB",
      displayStringKey: 'users_list.licenseColumn.MiCCB'
    }

    if (user.tags && user.tags.MiCCBusiness) {
      return Promise.resolve(userLicense);
    }

    return Promise.resolve(undefined);
  }

  canFilterUsersOnProduct?(account: Account): boolean {
    return true;
  }

  async filterUsersBasedOnProduct(account: Account, users: User[], filter: any[]): Promise<User[]> {
    let newUsersList = new Array<User>();

    users.forEach(user => {
      if (user.tags && user.tags.MiCCBusiness) {
        newUsersList.push(user);
      }
    })
    return newUsersList;
  }
}